"use client";

import React from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import Link from "next/link";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { api } from "~/trpc/react";
import { Sparkles } from "lucide-react";
import { CommentRatings } from "../chat/components/rating";
import { Skeleton } from "../ui/skeleton";
import { Badge } from "../ui/badge";

export const AiTherapistPromotionBase = ({ title }: { title: string }) => {
  const { data: therapist, isLoading } =
    api.therapistRouter.getTherapist.useQuery({
      slug: "tijana-ai-terapeut",
    });

  const rating = therapist?.rating ?? 0;
  const relationshipCount = therapist?.relationshipCount;

  if (isLoading) {
    return (
      <Card className="mt-8 p-6">
        <div className="flex flex-col items-center gap-6">
          <Skeleton className="h-24 w-24 rounded-full" />
          <div className="w-full space-y-4 text-center">
            <Skeleton className="mx-auto h-8 w-[250px]" />
            <Skeleton className="mx-auto h-4 w-[200px]" />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="relative mt-8 overflow-hidden bg-editorial-quiet p-6 shadow-lg">
      <div className="relative z-10 flex flex-col items-center gap-8">
        <div className="relative">
          <Avatar className="h-24 w-24 border-2 border-primary-foreground/20 shadow-md">
            <AvatarImage
              src={therapist?.user.image ?? ""}
              alt={therapist?.user.name ?? ""}
            />
            <AvatarFallback>AI</AvatarFallback>
          </Avatar>
          <div className="absolute -right-1 -top-1 rounded-full bg-primary-foreground p-1.5 shadow-xs">
            <Sparkles className="h-3.5 w-3.5 rotate-6 text-brand" />
          </div>
        </div>

        <div className="flex flex-col items-center gap-6">
          <div className="space-y-2 text-center">
            <h3 className="text-xl font-bold text-brand-forest sm:text-2xl">
              {title}
            </h3>
            <p className="text-base text-muted-foreground">
              Upoznaj Tijanu, našeg AI psihologa dostupnog 24/7
            </p>
          </div>

          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CommentRatings
                rating={rating}
                variant="yellow"
                disabled
                showText={false}
                fill
                className="scale-110"
              />
              <span>{relationshipCount}+ sesija</span>
            </div>

            <div className="flex w-full flex-col items-center gap-3">
              <Link href="/tijana" className="w-full">
                <Button className="w-full shadow-xs sm:w-auto">
                  Započni besplatan razgovor
                </Button>
              </Link>
              <p className="text-sm font-medium text-brand">
                Prvih 40 poruka sa Tijanom je besplatno.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {[
            "Dostupna 24/7",
            "Bez zakazivanja",
            "Trenutni odgovori",
            "Potpuna diskrecija",
          ].map((feature) => (
            <Badge
              key={feature}
              variant="outline"
              className="bg-primary-foreground/80 text-xs font-medium text-brand shadow-xs ring-1 ring-brand/15 backdrop-blur-xs"
            >
              {feature}
            </Badge>
          ))}
        </div>
      </div>
    </Card>
  );
};

export const AiTherapistPromotion = () => {
  const { data: therapist, isLoading } =
    api.therapistRouter.getTherapist.useQuery({
      slug: "tijana-ai-terapeut",
    });

  const rating = therapist?.rating ?? 0;
  const relationshipCount = therapist?.relationshipCount;

  if (isLoading) {
    return (
      <Card className="mt-8 p-6">
        <div className="flex gap-6">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-8 w-[300px]" />
            <Skeleton className="mt-2 h-4 w-[200px]" />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="relative mt-8 overflow-hidden bg-editorial-quiet p-6 shadow-lg sm:p-8">
      <div className="relative z-10">
        <div className="flex items-start gap-8">
          <div className="relative hidden shrink-0 md:block">
            <Avatar className="h-[72px] w-[72px] border-2 border-primary-foreground/20 shadow-md">
              <AvatarImage
                src={therapist?.user.image ?? ""}
                alt={therapist?.user.name ?? ""}
              />
              <AvatarFallback>AI</AvatarFallback>
            </Avatar>
            <div className="absolute -right-1 -top-1 rounded-full bg-primary-foreground p-1.5 shadow-xs">
              <Sparkles className="h-3.5 w-3.5 rotate-6 text-brand" />
            </div>
          </div>

          <div className="flex-1 space-y-6">
            <div className="flex items-start justify-between gap-8">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-brand-forest">
                  Želiš da razgovaraš o svojim rezultatima odmah?
                </h3>
                <p className="text-base text-muted-foreground">
                  Upoznaj Tijanu, našeg AI psihologa dostupnog 24/7
                </p>
              </div>

              <div className="hidden items-center gap-2 whitespace-nowrap text-sm text-muted-foreground md:flex">
                <CommentRatings
                  rating={rating}
                  variant="yellow"
                  disabled
                  showText={false}
                  fill
                  className="scale-110"
                />
                <span>{relationshipCount}+ sesija</span>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <Link href="/tijana">
                  <Button className="shadow-xs">
                    Započni besplatan razgovor
                  </Button>
                </Link>
                <p className="text-sm font-medium text-brand">
                  Prvih 40 poruka sa Tijanom je besplatno.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {[
                  "Dostupna 24/7",
                  "Bez zakazivanja",
                  "Trenutni odgovori",
                  "Potpuna diskrecija",
                ].map((feature) => (
                  <Badge
                    key={feature}
                    variant="outline"
                    className="bg-primary-foreground/80 text-xs font-medium text-brand shadow-xs ring-1 ring-brand/15 backdrop-blur-xs"
                  >
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const AiTherapistPromotionBlog = ({
  topic = "Želiš da pričaš sa terapeutom o ovoj temi",
}: {
  topic?: string;
}) => {
  return <AiTherapistPromotionBase title={topic} />;
};
